/**
 * MEL Service
 * Handles MEL (Minimum Equipment List) API operations
 */

import { api } from './api';
import type {
  MelDocument,
  MelDocumentListResponse,
  ActiveMel,
  ActiveMelListResponse,
  ActiveMelResponse,
  SmartSearchRequest,
  SmartSearchResponse,
  SmartSearchApiResponse,
  IssueMelRequest,
  ClearMelRequest,
} from '../types/mel';

/**
 * Perform smart search for MEL items
 */
export const smartSearch = async (request: SmartSearchRequest): Promise<SmartSearchResponse> => {
  const response = await api.post<SmartSearchApiResponse>('/mel/smart-search', request);
  return response.data;
};

/**
 * Get all MEL documents
 */
export const getMelDocuments = async (): Promise<MelDocument[]> => {
  const response = await api.get<MelDocumentListResponse>('/mel/documents');
  return response.data;
};

/**
 * Get all active MELs across all aircraft
 */
export const getAllActiveMels = async (): Promise<ActiveMel[]> => {
  const response = await api.get<ActiveMelListResponse>('/mel/active');
  return response.data;
};

/**
 * Get active MELs for a specific aircraft
 */
export const getActiveMelsByAircraft = async (aircraftId: number): Promise<ActiveMel[]> => {
  const response = await api.get<ActiveMelListResponse>(`/mel/active/aircraft/${aircraftId}`);
  return response.data;
};

/**
 * Get active MELs for an aircraft by tail number
 */
export const getActiveMelsByTailNumber = async (tailNumber: string): Promise<ActiveMel[]> => {
  const response = await api.get<ActiveMelListResponse>(`/mel/active/tail/${encodeURIComponent(tailNumber)}`);
  return response.data;
};

/**
 * Get MELs that are expiring soon
 */
export const getExpiringMels = async (days: number = 3): Promise<ActiveMel[]> => {
  const response = await api.get<ActiveMelListResponse>(`/mel/active/expiring?days=${days}`);
  return response.data;
};

/**
 * Issue a new MEL for an aircraft
 */
export const issueMel = async (request: IssueMelRequest): Promise<ActiveMel> => {
  const response = await api.post<ActiveMelResponse>('/mel/active', request);
  return response.data;
};

/**
 * Clear an active MEL
 */
export const clearMel = async (melId: number, request: ClearMelRequest): Promise<ActiveMel> => {
  const response = await api.post<ActiveMelResponse>(`/mel/active/${melId}/clear`, request);
  return response.data;
};

/**
 * Calculate days until MEL expires
 */
export const getDaysUntilExpiry = (expiresAt: string | null): number | null => {
  if (!expiresAt) return null;
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffMs = expiry.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

/**
 * Format expiry status for display
 */
export const formatExpiryStatus = (expiresAt: string | null): string => {
  if (!expiresAt) return 'No time limit';
  const days = getDaysUntilExpiry(expiresAt);
  if (days === null) return 'No time limit';
  if (days < 0) return 'EXPIRED';
  if (days === 0) return 'Expires today';
  if (days === 1) return 'Expires tomorrow';
  return `${days} days remaining`;
};

/**
 * Check if MEL is expiring soon (within specified days)
 */
export const isExpiringSoon = (expiresAt: string | null, threshold: number = 3): boolean => {
  const days = getDaysUntilExpiry(expiresAt);
  if (days === null) return false;
  return days <= threshold && days >= 0;
};

/**
 * Check if MEL has expired
 */
export const isExpired = (expiresAt: string | null): boolean => {
  const days = getDaysUntilExpiry(expiresAt);
  if (days === null) return false;
  return days < 0;
};

export default {
  smartSearch,
  getMelDocuments,
  getAllActiveMels,
  getActiveMelsByAircraft,
  getActiveMelsByTailNumber,
  getExpiringMels,
  issueMel,
  clearMel,
  getDaysUntilExpiry,
  formatExpiryStatus,
  isExpiringSoon,
  isExpired,
};
